import { useEffect, useLayoutEffect, useRef, useState } from "react";
import clsx from "clsx";
import { createPortal } from "react-dom";
import { useHoverCapable } from "@/hooks/useHoverCapable";
import {
  PROJECT_WORKFLOW_STATUS_LABELS,
  projectWorkflowStatusBadgeClass,
} from "@/lib/projects";
import { formatDateDe } from "./dateUtils";
import { formatMontageplanDayHours } from "./useMontageplanDailyHours";
import {
  bookedSpanIsPaid,
  bookedWorkflowFillClass,
  type BookedAssignmentSpan,
  type BookedColorMode,
} from "./bookedAssignments";
import {
  MP_TOOLTIP_SHOW_DELAY_MS,
  MP_TOOLTIP_Z_INDEX_CLASS,
} from "./layoutConstants";

interface BookedAssignmentTooltipProps {
  span: BookedAssignmentSpan;
  anchorRef: React.RefObject<HTMLElement | null>;
  pointerX?: number | null;
  visible: boolean;
  colorMode: BookedColorMode;
}

export function BookedAssignmentTooltip({
  span,
  anchorRef,
  pointerX,
  visible,
  colorMode,
}: BookedAssignmentTooltipProps) {
  const hoverCapable = useHoverCapable();
  const [shown, setShown] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const panelRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (!hoverCapable || !visible) {
      setShown(false);
      return;
    }
    timerRef.current = window.setTimeout(() => setShown(true), MP_TOOLTIP_SHOW_DELAY_MS);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [hoverCapable, visible, span.key]);

  useLayoutEffect(() => {
    if (!shown || !anchorRef.current || !panelRef.current) return;
    const anchor = anchorRef.current.getBoundingClientRect();
    const panel = panelRef.current.getBoundingClientRect();
    const pad = 8;
    const anchorX =
      pointerX != null
        ? Math.min(Math.max(pointerX, anchor.left), anchor.right)
        : anchor.left + anchor.width / 2;
    let left = anchorX - panel.width / 2;
    let top = anchor.top - panel.height - 10;
    if (left < pad) left = pad;
    if (left + panel.width > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - panel.width - pad);
    }
    if (top < pad) {
      top = anchor.bottom + 10;
    }
    setPosition({ top, left });
  }, [shown, anchorRef, span, pointerX]);

  if (!shown) return null;

  const hoursLabel = formatMontageplanDayHours(span.hours);
  const secondary = span.workflowStatusSecondary;
  const paid = bookedSpanIsPaid(span);
  const showStatusColor = colorMode === "status";

  return createPortal(
    <div
      ref={panelRef}
      className={clsx(
        "mp-tooltip pointer-events-none fixed max-w-[min(92vw,24rem)] rounded-ios border border-separator bg-card p-3 shadow-ios-md ring-1 ring-black/5",
        MP_TOOLTIP_Z_INDEX_CLASS,
      )}
      style={{ top: position.top, left: position.left }}
      role="tooltip"
    >
      <div className="flex items-start gap-2">
        <span
          className={clsx(
            "mt-0.5 inline-block h-3 w-3 shrink-0 rounded-sm",
            showStatusColor
              ? bookedWorkflowFillClass(span.workflowStatus)
              : span.deviates
                ? "mp-legend-swatch--booked-deviates"
                : "mp-legend-swatch--booked",
          )}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-label">{span.title}</div>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <span className={projectWorkflowStatusBadgeClass(span.workflowStatus)}>
              {PROJECT_WORKFLOW_STATUS_LABELS[span.workflowStatus]}
            </span>
            {secondary ? (
              <span className={projectWorkflowStatusBadgeClass(secondary)}>
                {PROJECT_WORKFLOW_STATUS_LABELS[secondary]}
              </span>
            ) : null}
            {paid ? (
              <span className="rounded bg-status-green/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-status-green">
                Vollständig bezahlt
              </span>
            ) : null}
          </div>
          <div className="mt-1 text-xs text-label-secondary">
            {formatDateDe(span.dateFrom)}
            {span.dateFrom !== span.dateTo ? ` – ${formatDateDe(span.dateTo)}` : ""}
            {` · ${hoursLabel}`}
          </div>
          {span.detail ? (
            <div className="mt-1 text-xs leading-snug text-label-secondary">{span.detail}</div>
          ) : null}
        </div>
      </div>

      <div className="mt-2.5 border-t border-separator pt-2">
        <div className="text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
          Gebuchte Zeit
        </div>
        <p className="mt-1 text-xs leading-relaxed text-label-secondary">
          {span.deviates
            ? "Abweichung von der Planung. Die Stunden liegen auf einem Tag ohne passenden Einsatz."
            : "Gebucht auf einen geplanten Einsatz."}
        </p>
      </div>

      {span.beschreibung ? (
        <div className="mt-2.5 border-t border-separator pt-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-label-tertiary">
            Projekt
          </div>
          <p className="mt-1 line-clamp-4 whitespace-pre-wrap text-xs leading-relaxed text-label-secondary">
            {span.beschreibung}
          </p>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
